import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from '@spacegame/ui';
import { useSettings, useSettingsActions } from '../game/SettingsProvider';
import { TOUR_STEPS } from './tour';
import type { TourViewId } from './types';

export interface TutorialContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;

  /** First-run welcome screen — shown once per device until dismissed. */
  showWelcome: boolean;
  dismissWelcome: () => void;

  isHelpOpen: boolean;
  openHelp: (chapterId?: string) => void;
  closeHelp: () => void;
  activeChapterId: string | null;
  setActiveChapterId: (id: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  tourActive: boolean;
  tourStepIndex: number;
  tourStepCount: number;
  startTour: () => void;
  stopTour: () => void;
  nextTourStep: () => void;
  prevTourStep: () => void;
  /** The view the active tour step wants on screen — `App.tsx` watches this. */
  pendingView: TourViewId | null;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const settings = useSettings();
  const settingsActions = useSettingsActions();

  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tourActive, setTourActive] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const value = useMemo<TutorialContextValue>(() => {
    const openHelp = (chapterId?: string) => {
      setTourActive(false);
      setActiveChapterId(chapterId ?? null);
      setSearchQuery('');
      setIsHelpOpen(true);
    };
    const closeHelp = () => setIsHelpOpen(false);

    const startTour = () => {
      setIsHelpOpen(false);
      setTourStepIndex(0);
      setTourActive(true);
    };
    const stopTour = () => setTourActive(false);
    const nextTourStep = () =>
      setTourStepIndex((index) => {
        const next = index + 1;
        if (next >= TOUR_STEPS.length) {
          setTourActive(false);
          return index;
        }
        return next;
      });
    const prevTourStep = () => setTourStepIndex((index) => Math.max(0, index - 1));

    const dismissWelcome = () => {
      setDismissedThisSession(true);
      settingsActions.setTutorialSeen(true);
    };

    return {
      locale: settings.locale,
      setLocale: settingsActions.setLocale,
      showWelcome: !settings.tutorialSeen && !dismissedThisSession,
      dismissWelcome,
      isHelpOpen,
      openHelp,
      closeHelp,
      activeChapterId,
      setActiveChapterId,
      searchQuery,
      setSearchQuery,
      tourActive,
      tourStepIndex,
      tourStepCount: TOUR_STEPS.length,
      startTour,
      stopTour,
      nextTourStep,
      prevTourStep,
      pendingView: tourActive ? (TOUR_STEPS[tourStepIndex]?.navTarget ?? null) : null,
    };
  }, [settings.locale, settings.tutorialSeen, settingsActions, dismissedThisSession, isHelpOpen, activeChapterId, searchQuery, tourActive, tourStepIndex]);

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}

export function useTutorial(): TutorialContextValue {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used inside <TutorialProvider>.');
  return ctx;
}
